import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Loader } from 'lucide-react';
import { TimeSlot, Availability } from '../../types';
import { useHourFormat } from '../../hooks/useHourFormat';
import { formatTimeFromDate } from '../../utils/locale';
import { isSlotFullySelected } from '../../hooks/useLeaderboard';
import { SlotParticipantDetail } from './SlotParticipantDetail';

interface AltSlotCardProps {
  slot: TimeSlot;
  rank: number;
  totalParticipants: number;
  allParticipantNames: string[];
  participantName: string | null;
  availability: Availability[];
  isSelecting: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (slot: TimeSlot) => void;
}

const getLocaleFromLanguage = (lang: string): string => {
  const localeMap: Record<string, string> = {
    en: 'en-US',
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
    ru: 'ru-RU',
  };
  return localeMap[lang] || 'en-US';
};

export function AltSlotCard({
  slot,
  rank,
  totalParticipants,
  allParticipantNames,
  participantName,
  availability,
  isSelecting,
  isOpen,
  onToggle,
  onSelect,
}: AltSlotCardProps) {
  const { t, i18n } = useTranslation();
  const { hourFormat } = useHourFormat();
  const locale = getLocaleFromLanguage(i18n.language);

  const startDate = new Date(slot.utc_start * 1000);
  const endDate = new Date(slot.utc_end * 1000);

  const dow = startDate.toLocaleDateString(locale, { weekday: 'short' }).toUpperCase();
  const day = startDate
    .toLocaleDateString(locale, { month: 'short', day: 'numeric' })
    .toUpperCase();
  const startHour = formatTimeFromDate(startDate, i18n.language, hourFormat);
  const endHour = formatTimeFromDate(endDate, i18n.language, hourFormat);

  const total = totalParticipants > 0 ? totalParticipants : slot.count;
  const pct = total > 0 ? Math.round((slot.count / total) * 100) : 0;

  const isFullySelected = participantName
    ? isSlotFullySelected(slot, participantName, availability)
    : false;

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isFullySelected || isSelecting || !participantName) return;
    onSelect(slot);
  };

  return (
    <div className="rounded-[14px] border border-room-border bg-room-surface px-3.5 py-3">
      <div className="flex items-center gap-2.5">
        <span className="w-[18px] shrink-0 font-mono text-xs font-bold text-room-text-faint">
          #{rank}
        </span>
        <div className="flex min-w-[56px] shrink-0 flex-col leading-tight">
          <span className="text-[11px] font-bold text-room-text-faint">{dow}</span>
          <span className="text-[12.5px] font-semibold text-room-text-dim">{day}</span>
        </div>
        <span className="flex-1 text-[15.5px] font-bold">
          {startHour} – {endHour}
        </span>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            onToggle();
          }}
          aria-expanded={isOpen}
          aria-label={t('leaderboard.viewParticipants')}
          className={`flex shrink-0 items-center gap-1 rounded-full border border-room-border bg-room-surface-2 px-2.5 py-1.5 hover:bg-room-surface-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-room-accent ${isOpen ? 'open' : ''}`}
        >
          <span className="font-mono text-xs font-bold text-room-accent">{pct}%</span>
          <ChevronDown
            size={9}
            strokeWidth={3}
            className={`text-room-text-dim transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
        <button
          type="button"
          onClick={handleSelect}
          disabled={isFullySelected || isSelecting || !participantName}
          aria-label={t('leaderboard.selectSlot')}
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border-[1.5px] border-room-border-strong bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-room-accent ${
            isFullySelected ? 'border-room-accent bg-room-accent/20' : ''
          }`}
        >
          {isSelecting ? (
            <Loader size={13} className="animate-spin text-room-accent" />
          ) : isFullySelected ? (
            <Check size={13} strokeWidth={3} className="text-room-accent" />
          ) : null}
        </button>
      </div>
      <SlotParticipantDetail
        participantNames={allParticipantNames}
        availableNames={slot.participants}
        currentParticipantName={participantName}
        isOpen={isOpen}
      />
    </div>
  );
}
